import mixpanel from '@/configs/mixpanel';
import { getCurrentTimestamp } from '@/utils';

export const PROBABILITY_MIXPANEL_EVENTS = {
	HISTORY_TAB_VIEWED: '[HISTORY TAB VIEWED]',
	SETTLED_MATCH_OPENED: '[SETTLED MATCH OPENED]',
	LEADERBOARD_VIEWED: '[LEADERBOARD VIEWED]',
	LEADERBOARD_FILTERED: '[LEADERBOARD FILTERED]',
	LIVE_LEADERBOARD_VIEWED: '[LIVE LEADERBOARD VIEWED]',
	BRIER_BREAKDOWN_VIEWED: '[BRIER BREAKDOWN VIEWED]',
	EMAIL_LINK_CLICKED: '[EMAIL LINK CLICKED]',
	COMPETITOR_PROFILE_PREVIEWED: '[COMPETITOR PROFILE PREVIEWED]',
	COMPETITOR_FOLLOW_CLICKED: '[COMPETITOR FOLLOW CLICKED]',
	COMPETITOR_UNFOLLOW_CLICKED: '[COMPETITOR UNFOLLOW CLICKED]',
	ACHIEVEMENTS_VIEWED: '[ACHIEVEMENTS VIEWED]',
	ACHIEVEMENT_SHARE_CLICKED: '[ACHIEVEMENT SHARE CLICKED]',
	LOCKED_IN_SHARE_CLICKED: '[LOCKED IN SHARE CLICKED]',
	EVENT_SHARE_CLICKED: '[EVENT SHARE CLICKED]',
	PROFILE_EDIT_STARTED: '[PROFILE EDIT STARTED]',
	PROFILE_UPDATED: '[PROFILE UPDATED]',
	PERFORMANCE_SUMMARY_CLICKED: '[PERFORMANCE SUMMARY CLICKED]',
	PERFORMANCE_SUMMARY_TAB_CLICKED: '[PERFORMANCE SUMMARY TAB CLICKED]',
	APP_REVIEW_PROMPT_REQUESTED: '[APP REVIEW PROMPT REQUESTED]',
	SEE_YOUR_PERFORMANCE_CLICKED: '[SEE YOUR PERFORMANCE CLICKED]',
	VIEW_FULL_LEADERBOARD_CLICKED: '[VIEW FULL LEADERBOARD CLICKED]',
	SAVE_PREFERENCES_CLICKED: '[SAVE PREFERENCES CLICKED]',
	SUBSCRIBE_FOR_FREE_CLICKED: '[SUBSCRIBE FOR FREE CLICKED]',
	RECAP_VIDEO_CLICKED: '[RECAP VIDEO CLICKED]',
	BOT_GENERATION_STARTED: '[BOT GENERATION STARTED]',
	BOT_GENERATION_COMPLETED: '[BOT GENERATION COMPLETED]',
	BOT_API_REJECTED: '[BOT API REJECTED]',
	BOTS_TAB_VIEWED: '[BOTS TAB VIEWED]',
	COMPARE_YOUR_BOTS_CLICKED: '[COMPARE YOUR BOTS CLICKED]',
	ANSWERING_MODE_TOGGLED: '[ANSWERING MODE TOGGLED]',
} as const;

// Map bracketed display name → snake_case BI spec name for dataLayer dual-write.
// Per the WCC BI Events & Analytics doc, every event should hit both Mixpanel
// (display name) and GTM dataLayer (snake_case). Matches the existing pattern
// from use-auth-flow.hook.ts (`mixpanel.track('[AUTH COMPLETED]')` +
// `window.dataLayer.push({ event: 'auth_completed' })`).
const DATALAYER_EVENT_NAMES: Record<string, string> = {
	'[HISTORY TAB VIEWED]': 'history_tab_viewed',
	'[SETTLED MATCH OPENED]': 'settled_match_opened',
	'[LEADERBOARD VIEWED]': 'leaderboard_viewed',
	'[LEADERBOARD FILTERED]': 'leaderboard_filtered',
	'[LIVE LEADERBOARD VIEWED]': 'live_leaderboard_viewed',
	'[BRIER BREAKDOWN VIEWED]': 'brier_breakdown_viewed',
	'[EMAIL LINK CLICKED]': 'email_link_clicked',
	'[COMPETITOR PROFILE PREVIEWED]': 'competitor_profile_previewed',
	'[COMPETITOR FOLLOW CLICKED]': 'competitor_follow_clicked',
	'[COMPETITOR UNFOLLOW CLICKED]': 'competitor_unfollow_clicked',
	'[ACHIEVEMENTS VIEWED]': 'achievements_viewed',
	'[ACHIEVEMENT SHARE CLICKED]': 'achievement_share_clicked',
	'[LOCKED IN SHARE CLICKED]': 'locked_in_share_clicked',
	'[EVENT SHARE CLICKED]': 'event_share_clicked',
	'[PROFILE EDIT STARTED]': 'profile_edit_started',
	'[PROFILE UPDATED]': 'profile_updated',
	'[PERFORMANCE SUMMARY CLICKED]': 'performance_summary_clicked',
	'[PERFORMANCE SUMMARY TAB CLICKED]': 'performance_summary_tab_clicked',
	'[APP REVIEW PROMPT REQUESTED]': 'app_review_prompt_requested',
	'[SEE YOUR PERFORMANCE CLICKED]': 'see_your_performance_clicked',
	'[VIEW FULL LEADERBOARD CLICKED]': 'view_full_leaderboard_clicked',
	'[SAVE PREFERENCES CLICKED]': 'save_preferences_clicked',
	'[SUBSCRIBE FOR FREE CLICKED]': 'subscribe_for_free_clicked',
	'[RECAP VIDEO CLICKED]': 'recap_video_clicked',
	'[BOT GENERATION STARTED]': 'bot_generation_started',
	'[BOT GENERATION COMPLETED]': 'bot_generation_completed',
	'[BOT API REJECTED]': 'bot_api_rejected',
	'[BOTS TAB VIEWED]': 'bots_tab_viewed',
	'[COMPARE YOUR BOTS CLICKED]': 'compare_your_bots_clicked',
	'[ANSWERING MODE TOGGLED]': 'answering_mode_toggled',
};

type ProbabilityMixpanelEventName =
	(typeof PROBABILITY_MIXPANEL_EVENTS)[keyof typeof PROBABILITY_MIXPANEL_EVENTS];

type ProbabilityAnalyticsUser = {
	id?: string;
	email?: string | null;
	username?: string | null;
};

type TrackProbabilityEventOptions = Record<string, unknown> & {
	eventId?: string | null;
	eventName?: string | null;
	user?: ProbabilityAnalyticsUser | null;
};

const getMessageText = (message: unknown): string | undefined => {
	if (typeof message === 'string' && message.trim()) return message;
	if (Array.isArray(message)) {
		const messages = message.filter((item): item is string => typeof item === 'string');
		return messages.length ? messages.join(', ') : undefined;
	}
	return undefined;
};

export type LeaderboardAnalyticsFilterType =
	| 'Global'
	| 'Match'
	| 'Players'
	| 'Bots'
	| 'Friends'
	| 'Country'
	| 'NHSSAA';

export const getLeaderboardAnalyticsFilterType = (
	type?: string,
	scope: 'global' | 'match' = 'global'
): LeaderboardAnalyticsFilterType => {
	if (type === 'players') return 'Players';
	if (type === 'bots') return 'Bots';
	if (type === 'friends') return 'Friends';
	if (type === 'country') return 'Country';
	if (type === 'match') return 'Match';
	return scope === 'match' ? 'Match' : 'Global';
};

export function trackProbabilityMixpanelEvent(
	name: ProbabilityMixpanelEventName,
	{ eventId, eventName, user, ...extra }: TrackProbabilityEventOptions = {}
) {
	const enriched = {
		...extra,
		event_id: eventId,
		event_name: eventName,
		userId: user?.id,
		userEmail: user?.email,
		username: user?.username,
		timestamp: getCurrentTimestamp(),
	};
	mixpanel.track(name, enriched);
	// Dual-write to GTM dataLayer per WCC BI Events spec — same payload, snake_case name.
	const dataLayerName = DATALAYER_EVENT_NAMES[name];
	if (dataLayerName) {
		try {
			(window as any).dataLayer?.push?.({ event: dataLayerName, ...enriched });
		} catch {
			// never break the app from tracking
		}
	}
}

export function getProbabilityAnalyticsErrorReason(
	error: unknown,
	fallback = 'Unknown error'
): string {
	if (typeof error === 'string') return error;
	if (error instanceof Error) return error.message || fallback;
	if (!error || typeof error !== 'object') return fallback;

	const errorRecord = error as Record<string, unknown>;
	const data = errorRecord.data;

	if (data && typeof data === 'object') {
		const dataRecord = data as Record<string, unknown>;
		return (
			getMessageText(dataRecord.message) ||
			getMessageText(dataRecord.error) ||
			getMessageText(errorRecord.error) ||
			getMessageText(errorRecord.message) ||
			fallback
		);
	}

	return (
		getMessageText(data) ||
		getMessageText(errorRecord.error) ||
		getMessageText(errorRecord.message) ||
		fallback
	);
}
